import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Upload, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

interface DocumentMapping {
  filePath: string;
  title: string;
  department: 'legal_compliance' | 'finance' | 'marketing' | 'operations' | 'hr' | 'clinical';
  documentType: 'dpia' | 'dpa' | 'insurance' | 'terms_conditions' | 'privacy_policy' | 'claims_policy' | 'compliance_report' | 'contract' | 'financial' | 'marketing_material' | 'other';
  description: string;
  categoryName: string;
}

export function DocumentUploader() {
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState<{ success: string[]; failed: string[] }>({
    success: [],
    failed: [],
  });

  const legalDocuments: DocumentMapping[] = [
    {
      filePath: 'legal-docs/user-agreements/PRIVACY_POLICY.md',
      title: 'Privacy Policy',
      department: 'legal_compliance',
      categoryName: 'Legal & Compliance',
      documentType: 'privacy_policy',
      description: 'THERAPPI® Privacy Policy - GDPR compliant data protection policy',
    },
    {
      filePath: 'legal-docs/user-agreements/TERMS_AND_CONDITIONS.md',
      title: 'Terms and Conditions',
      department: 'legal_compliance',
      categoryName: 'Legal & Compliance',
      documentType: 'terms_conditions',
      description: 'THERAPPI® Terms and Conditions of service',
    },
    {
      filePath: 'legal-docs/user-agreements/THERAPPI_AI_DISCLAIMER.md',
      title: 'AI Disclaimer',
      department: 'legal_compliance',
      categoryName: 'Legal & Compliance',
      documentType: 'other',
      description: 'AI reflection limitations and professional therapy disclaimer',
    },
    {
      filePath: 'legal-docs/user-agreements/WHOOP_DATA_CONSENT.md',
      title: 'WHOOP Data Consent',
      department: 'legal_compliance',
      categoryName: 'Legal & Compliance',
      documentType: 'other',
      description: 'WHOOP integration data sharing consent and privacy notice',
    },
    {
      filePath: 'legal-docs/user-agreements/CRISIS_DISCLAIMER.md',
      title: 'Crisis Resources Disclaimer',
      department: 'clinical',
      categoryName: 'Clinical Safety',
      documentType: 'other',
      description: 'Emergency services information and crisis support disclaimer',
    },
    {
      filePath: 'legal-docs/AI_SAFETY_FRAMEWORK_IMPLEMENTATION.md',
      title: 'AI Safety Framework',
      department: 'clinical',
      categoryName: 'Clinical Safety',
      documentType: 'other',
      description: 'Comprehensive AI safety implementation and monitoring framework',
    },
    {
      filePath: 'legal-docs/CLAIMS_BOUNDARY_POLICY.md',
      title: 'Claims Boundary Policy',
      department: 'clinical',
      categoryName: 'Clinical Safety',
      documentType: 'claims_policy',
      description: 'Policy defining acceptable therapeutic claims and boundaries',
    },
    {
      filePath: 'legal-docs/CRISIS_MODE_IMPLEMENTATION.md',
      title: 'Crisis Mode Implementation',
      department: 'clinical',
      categoryName: 'Clinical Safety',
      documentType: 'other',
      description: 'Crisis detection and response implementation guide',
    },
    {
      filePath: 'legal-docs/SAFETY_INVESTIGATION_SOP.md',
      title: 'Safety Investigation SOP',
      department: 'clinical',
      categoryName: 'Clinical Safety',
      documentType: 'other',
      description: 'Standard operating procedure for safety incident investigations',
    },
    {
      filePath: 'legal-docs/COMPLIANCE_AUDIT_REPORT.md',
      title: 'Compliance Audit Report',
      department: 'legal_compliance',
      categoryName: 'Compliance & Audits',
      documentType: 'compliance_report',
      description: 'Regulatory compliance audit findings and recommendations',
    },
    {
      filePath: 'legal-docs/DPIA_TEMPLATE.md',
      title: 'DPIA Template',
      department: 'legal_compliance',
      categoryName: 'Compliance & Audits',
      documentType: 'dpia',
      description: 'Data Protection Impact Assessment template for GDPR compliance',
    },
    {
      filePath: 'legal-docs/DPA_SIGNING_CHECKLIST.md',
      title: 'DPA Signing Checklist',
      department: 'legal_compliance',
      categoryName: 'Compliance & Audits',
      documentType: 'dpa',
      description: 'Data Processing Agreement signing and review checklist',
    },
    {
      filePath: 'legal-docs/INSURANCE_PROCUREMENT_GUIDE.md',
      title: 'Insurance Procurement Guide',
      department: 'finance',
      categoryName: 'Business Operations',
      documentType: 'insurance',
      description: 'Guide for procuring necessary business and liability insurance',
    },
    {
      filePath: 'LAUNCH_READINESS_GUIDE.md',
      title: 'Launch Readiness Guide',
      department: 'operations',
      categoryName: 'Business Operations',
      documentType: 'other',
      description: 'Comprehensive launch readiness checklist and guide',
    },
    {
      filePath: 'legal-docs/SECURITY_FEATURES.md',
      title: 'Security Features Documentation',
      department: 'operations',
      categoryName: 'Technical Documentation',
      documentType: 'other',
      description: 'Technical documentation of security features and implementations',
    },
  ];

  const uploadDocuments = async () => {
    setUploading(true);
    setUploadResults({ success: [], failed: [] });
    const newResults = { success: [] as string[], failed: [] as string[] };

    try {
      // Upload each document
      for (const doc of legalDocuments) {
        try {
          // Ensure category exists
          const { data: existingCategory } = await supabase
            .from('business_document_categories')
            .select('id')
            .eq('name', doc.categoryName)
            .eq('department', doc.department)
            .maybeSingle();

          let categoryId = existingCategory?.id;

          if (!categoryId) {
            const { data: newCategory, error: categoryError } = await supabase
              .from('business_document_categories')
              .insert({
                name: doc.categoryName,
                department: doc.department,
                description: `${doc.categoryName} documents`,
              })
              .select('id')
              .single();

            if (categoryError) {
              console.error(`Error creating category ${doc.categoryName}:`, categoryError);
              throw categoryError;
            }
            categoryId = newCategory.id;
          }

          // Fetch the document content
          const response = await fetch(`/${doc.filePath}`);
          if (!response.ok) {
            throw new Error(`Failed to fetch ${doc.filePath}`);
          }
          
          const content = await response.text();
          const blob = new Blob([content], { type: 'text/markdown' });
          const fileName = `${doc.department}/${doc.title.toLowerCase().replace(/\s+/g, '-')}.md`;

          // Upload to storage
          const { error: uploadError } = await supabase.storage
            .from('business-documents')
            .upload(fileName, blob, {
              contentType: 'text/markdown',
              upsert: true,
            });

          if (uploadError) throw uploadError;

          // Create database record
          const { error: dbError } = await supabase
            .from('business_documents')
            .insert({
              title: doc.title,
              description: doc.description,
              category_id: categoryId,
              document_type: doc.documentType,
              file_path: fileName,
              version: 1,
              is_current_version: true,
              next_review_date: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString(),
            });

          if (dbError) throw dbError;

          newResults.success.push(doc.title);
        } catch (error) {
          console.error(`✗ Failed to upload ${doc.title}:`, error);
          newResults.failed.push(doc.title);
        }
      }

      setUploadResults(newResults);
      
      if (newResults.failed.length === 0) {
        toast.success(`Successfully uploaded ${newResults.success.length} documents`);
      } else {
        toast.warning(
          `Uploaded ${newResults.success.length} documents, ${newResults.failed.length} failed`
        );
      }
    } catch (error) {
      console.error('Error uploading documents:', error);
      toast.error('Failed to upload documents');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Upload Legal Documents</CardTitle>
        <CardDescription>
          Upload all legal and compliance documents from the legal-docs folder to the Business Documents system
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Alert>
          <AlertDescription>
            This will upload {legalDocuments.length} documents to the business-documents storage bucket
            and create database records for tracking versions and reviews.
          </AlertDescription>
        </Alert>

        <Button onClick={uploadDocuments} disabled={uploading} className="w-full">
          {uploading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Uploading {uploadResults.success.length + uploadResults.failed.length} / {legalDocuments.length}...
            </>
          ) : (
            <>
              <Upload className="mr-2 h-4 w-4" />
              Upload All Documents
            </>
          )}
        </Button>

        {(uploadResults.success.length > 0 || uploadResults.failed.length > 0) && (
          <div className="space-y-2 mt-4">
            {uploadResults.success.length > 0 && (
              <Alert className="border-green-200 bg-green-50">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription>
                  <strong className="text-green-800">Successfully uploaded ({uploadResults.success.length}):</strong>
                  <ul className="list-disc list-inside mt-2 text-sm text-green-700">
                    {uploadResults.success.map((doc) => (
                      <li key={doc}>{doc}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            {uploadResults.failed.length > 0 && (
              <Alert className="border-red-200 bg-red-50">
                <AlertCircle className="h-4 w-4 text-red-600" />
                <AlertDescription>
                  <strong className="text-red-800">Failed to upload ({uploadResults.failed.length}):</strong>
                  <ul className="list-disc list-inside mt-2 text-sm text-red-700">
                    {uploadResults.failed.map((doc) => (
                      <li key={doc}>{doc}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
